import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Table, Input, Pagination, PaginationItem, PaginationLink, Button } from 'reactstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInbox } from "@fortawesome/free-solid-svg-icons";
import { PostData } from '../../../services/FetchData';
import { toast } from 'react-toastify';
import DetailSkeleton from '../../../components/Skeleton/DetailSkeleton';
import { ProductContext } from '../../../store';
import Cookies from 'js-cookie';
import './AIAssistantProducts.scss';

const AIAssistantProducts = () => {
    const { currentWebsiteId } = useContext(ProductContext);
    const [searchTerm, setSearchTerm] = useState(''); // Input field value (not applied until Apply Filters is clicked)
    const [appliedSearchTerm, setAppliedSearchTerm] = useState(''); // Actual search term used in API calls
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [productsData, setProductsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalProducts, setTotalProducts] = useState(0);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    useEffect(() => {
        if(currentWebsiteId){
            fetchProducts(true);
        }
    }, [currentWebsiteId, appliedSearchTerm, currentPage, perPage]);
    

    // Helper function to generate pagination page numbers
    const getPaginationPages = () => {
        const delta = 2; // Number of pages to show on each side of current page
        const pages = [];
        
        // Add first page
        pages.push(1);
        
        // Add ellipsis if needed
        if (currentPage - delta > 2) {
            pages.push('...');
        }
        
        // Add pages around current page
        for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
            pages.push(i);
        }
        
        // Add ellipsis if needed
        if (currentPage + delta < totalPages - 1) {
            pages.push('...');
        }
        
        // Add last page (if more than 1 page)
        if (totalPages > 1) {
            pages.push(totalPages);
        }
        
        // Remove duplicates
        return [...new Set(pages)];
    };

    // Handler for Enable Intelligence toggle - updates ALL products with the same productId
    const handleIntelligenceToggle = async (productId, currentValue, uniqueId) => {
        const websiteId = currentWebsiteId;
        
        if (!websiteId) {
            toast.error("Website ID is missing");
            return;
        }

        if (!productId || productId.toString().trim() === '') {
            toast.error("Product ID is missing");
            return;
        }

        const newIntelligenceState = !currentValue;
        const trimmedProductId = productId.toString().trim();

        // Store original state for potential revert
        const originalProducts = [...productsData];

        try {
            // Update local state immediately for better UX (optimistic update)
            // Update ALL products with the same productId
            const updatedProducts = productsData.map(product => {
                const matchesProductId = (product.productId || '').toString().trim() === trimmedProductId;
                
                // Update all products with the same productId
                if (matchesProductId) {
                    return { ...product, widgetEnable: newIntelligenceState };
                }
                return product;
            });
            setProductsData(updatedProducts);

            // Prepare update payload - backend expects productId and widgetEnable
            const updatePayload = {
                productId: trimmedProductId,
                widgetEnable: newIntelligenceState
            };

            // Make API call to update backend (POST — production blocks PUT)
            const response = await PostData("ms1", "api/v1/aiassistant/updateWidgetEnable", updatePayload);

            if (response !== "Invalid" && response !== undefined) {
                if (response.status === "success") {
                    // Use the value we set (newIntelligenceState) as it's what we expect after backend updates all documents
                    // The optimistic update already set this, so we just need to ensure consistency
                    // Update ALL products with the same productId to match what we set
                    const updatedProducts = productsData.map(product => {
                        const matchesProductId = (product.productId || '').toString().trim() === trimmedProductId;
                        
                        // Update all products with the same productId to the new state
                        if (matchesProductId) {
                            return { ...product, widgetEnable: newIntelligenceState };
                        }
                        return product;
                    });
                    setProductsData(updatedProducts);
                    
                    // Refetch products from Elasticsearch to ensure we have the latest state
                    // This is important because the backend updates all documents with the same productId
                    // We need to wait for the backend to finish updating all documents
                    setTimeout(async () => {
                        try {
                            await fetchProducts(false); // Refetch without showing loading spinner
                        } catch (refetchError) {
                            console.error("Error refetching products after update:", refetchError);
                            // Don't show error to user, the update was successful
                        }
                    }, 1000); // Increased delay to ensure Elasticsearch index is refreshed and all documents are updated
                    
                    toast.success(`Widget ${newIntelligenceState ? 'enabled' : 'disabled'} successfully`);
                } else {
                    // Revert on error
                    setProductsData(originalProducts);
                    toast.error(response.message?.message || response.message || "Failed to update widget enable setting");
                }
            } else {
                // Revert on invalid response
                setProductsData(originalProducts);
                toast.error("Invalid response from server");
            }
        } catch (error) {
            // Revert on error
            setProductsData(originalProducts);
            toast.error("Failed to update widget enable setting");
        }
    };


    // Handler for clear all filters - clears all filters, resets search, and shows all products
    const handleClearFilters = () => {
        setSearchTerm(''); // Clear input field
        setAppliedSearchTerm(''); // Clear applied search to show all products
        setCurrentPage(1); // Reset to first page
        setIsFilterOpen(false); // Close sidebar after clearing
    };

    // Handler for apply filters - applies search term and closes sidebar
    const handleApplyFilters = () => {
        // Trim search term to remove any leading/trailing whitespace
        const trimmedSearchTerm = searchTerm.trim();
        setAppliedSearchTerm(trimmedSearchTerm);
        setCurrentPage(1); // Reset to first page when applying new filters
        setIsFilterOpen(false); // Close sidebar after applying filters
    };

    // Fetch products from Elasticsearch via API - extracted as a function so it can be called after updates
    const fetchProducts = async(showLoading = true) => {
            // Get websiteId from context or cookies
            const websiteId = currentWebsiteId;
            
            if (!websiteId) {
                toast.error("No website ID found");
                if (showLoading) setLoading(false);
                return;
            }
            
            if (showLoading) setLoading(true);
            try {
                // Trim search term to ensure proper matching (removes any whitespace)
                const trimmedSearchTerm = (appliedSearchTerm || '').trim();
                
                const postData = {
                    page: currentPage,
                    websiteId,
                    limit: perPage,
                    searchTerm: trimmedSearchTerm // Search by name or SKU - finds products matching this term
                };
                
                // Call API to fetch products from Elasticsearch
                const response = await PostData("ms1", "api/v1/aiassistant/products", postData);
                
                if (response !== "Invalid" && response !== undefined) {
                    if (response.status === "success") {
                        // Map API response to component format
                        // API returns: productId, name, sku, widgetEnable (boolean), createdAt
                        // Properly handle boolean values - show actual persisted status from backend
                        const mappedProducts = (response.data.products || []).map((product, idx) => {
                            // Convert widgetEnable to proper boolean
                            // Handle: true, false, "true", "false", undefined, null
                            let widgetEnable = true; // Default to true
                            if (product.widgetEnable === false || product.widgetEnable === 'false' || product.widgetEnable === 0) {
                                widgetEnable = false;
                            } else if (product.widgetEnable === true || product.widgetEnable === 'true' || product.widgetEnable === 1) {
                                widgetEnable = true;
                            } else if (product.widgetEnable !== undefined && product.widgetEnable !== null) {
                                // For any other truthy value, set to true
                                widgetEnable = Boolean(product.widgetEnable);
                            }
                            // If undefined or null, keep default true
                            
                            return {
                                productId: product.productId || '',
                                name: product.parentName || product.name || '',
                                sku: product.parentSku || product.sku || product.productId || '',
                                widgetEnable: widgetEnable,
                                createdAt: product.createdAt || '',
                                updatedAt: product.updatedAt || product.createdAt || '', // Use updatedAt if available, fallback to createdAt
                                uniqueId: `${product.productId || ''}_${idx}` // Unique ID for React key (stable across renders)
                            };
                        });
                        
                        // Normalize widgetEnable: All products with the same productId should have the same widgetEnable value
                        // Since backend may not update all documents, use the most recent document's value
                        // (Most recent = latest createdAt or updatedAt timestamp)
                        const productIdStatusMap = new Map(); // Map<productId, {widgetEnable, timestamp}>
                        
                        // First pass: find the most recent widgetEnable value for each productId
                        mappedProducts.forEach(product => {
                            const productIdKey = (product.productId || '').toString().trim();
                            if (productIdKey) {
                                // Parse timestamp - prefer updatedAt over createdAt (more accurate for recent updates)
                                let timestamp = 0;
                                const dateString = product.updatedAt || product.createdAt;
                                if (dateString) {
                                    try {
                                        timestamp = new Date(dateString).getTime();
                                    } catch (e) {
                                        timestamp = 0;
                                    }
                                }
                                
                                // If we haven't seen this productId, or this one is more recent, update it
                                if (!productIdStatusMap.has(productIdKey)) {
                                    productIdStatusMap.set(productIdKey, {
                                        widgetEnable: product.widgetEnable,
                                        timestamp: timestamp
                                    });
                                } else {
                                    const existing = productIdStatusMap.get(productIdKey);
                                    if (timestamp > existing.timestamp) {
                                        // This document is more recent, use its value
                                        productIdStatusMap.set(productIdKey, {
                                            widgetEnable: product.widgetEnable,
                                            timestamp: timestamp
                                        });
                                    }
                                }
                            }
                        });
                        
                        // Second pass: normalize all products with the same productId to have the same widgetEnable
                        // Use the most recent document's value for each productId
                        const normalizedProducts = mappedProducts.map(product => {
                            const productIdKey = (product.productId || '').toString().trim();
                            if (productIdKey && productIdStatusMap.has(productIdKey)) {
                                return {
                                    ...product,
                                    widgetEnable: productIdStatusMap.get(productIdKey).widgetEnable
                                };
                            }
                            return product;
                        });
                        
                        setProductsData(normalizedProducts);
                        setTotalProducts(response.data.total || normalizedProducts.length);
                    } else if (response.status === "failure" || response.status === "not_found_error") {
                        const errorMessage = response.message?.message || response.message || "Failed to fetch products";
                        toast.error(errorMessage);
                        setProductsData([]);
                        setTotalProducts(0);
                    } else if (response.status === "server_error" || response.status === "api_error" || response.status === "application_error") {
                        toast.error(response.message?.message || "Server error occurred");
                        setProductsData([]);
                        setTotalProducts(0);
                    } else {
                        toast.error(response.message?.message || "Failed to fetch products");
                        setProductsData([]);
                        setTotalProducts(0);
                    }
                } else {
                    toast.error("Invalid response from server");
                    setProductsData([]);
                    setTotalProducts(0);
                }
            } catch (error) {
                if (showLoading) toast.error("Failed to fetch products from Elasticsearch. Please check your backend connection.");
                setProductsData([]);
                setTotalProducts(0);
            } finally {
                if (showLoading) setLoading(false);
            }
    };

    
    // Calculate pagination based on backend total
    const totalPages = Math.ceil(totalProducts / perPage);
    const startIndex = (currentPage - 1) * perPage;
    const endIndex = Math.min(startIndex + productsData.length, totalProducts);

    return (
        <React.Fragment>
            <div className="ai-assistant-products">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-start mb-4">
                    <div>
                        <h4 className="mb-1">Products</h4>
                        <p className="text-muted mb-0">
                            Manage your products and enable AI intelligence for better search results
                        </p>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <Button
                            className="filter-btn"
                            color="outline-secondary"
                            onClick={() => {
                                // Sync input field with currently applied search when opening sidebar
                                if (!isFilterOpen) {
                                    setSearchTerm(appliedSearchTerm);
                                }
                                setIsFilterOpen(!isFilterOpen);
                            }}
                        >
                            <i className="fa fa-filter"></i>
                            Filter
                        </Button>
                    </div>
                </div>

                {/* Products Table */}
                {loading ? (
                    <DetailSkeleton />
                ) : (
                    <React.Fragment>
                        <div className="table-responsive">
                            <Table className="data-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>SKU</th>
                                        <th>Product Name</th>
                                        <th>Enable Intelligence</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {productsData.length > 0 ? (
                                        productsData.map((product, index) => (
                                            <tr key={product.uniqueId || `${product.productId}-${index}`}>
                                                <td>
                                                    <div className="searchItemStatusWrp">
                                                        {product.productId || 'N/A'}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="searchItemStatusWrp">
                                                        {product.sku || 'N/A'}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="searchItemStatusWrp">
                                                        <div className="text-truncate" title={product.name || product.sku || 'N/A'}>
                                                            {product.name || product.sku || 'N/A'}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="searchItemStatusWrp">
                                                        <div className="form-check form-switch">
                                                            <Input
                                                                className="form-check-input toggle-switch"
                                                                type="checkbox"
                                                                id={`intelligence-${product.uniqueId || `${product.productId}-${index}`}`}
                                                                checked={product.widgetEnable || false}
                                                                onChange={() => handleIntelligenceToggle(product.productId, product.widgetEnable || false, product.uniqueId)}
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={4} className="text-center p-5">
                                                <div className="d-flex flex-column align-items-center">
                                                    <FontAwesomeIcon icon={faInbox} style={{ fontSize: '48px', color: '#dee2e6' }} />
                                                    <p className="text-muted mt-3 mb-0">No Products Found</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                        
                        {/* Pagination inside the card */}
                        {totalProducts > 0 && (
                            <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-3 mt-3 pt-3" style={{borderTop: '1px solid #e9ecef'}}>
                                <div className="d-flex align-items-center gap-2">
                                    <span className="text-muted">Show</span>
                                    <Input
                                        type="select"
                                        value={perPage}
                                        onChange={(e) => setPerPage(Number(e.target.value))}
                                        style={{ width: '80px', fontSize: '14px' }}
                                        className="form-control-sm"
                                    >
                                        <option value={10}>10</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </Input>
                                    <span className="text-muted">per page</span>
                                </div>
                                
                                {/* Center: Showing text */}
                                <div className="flex-grow-1 text-center">
                                    <span className="text-muted" style={{fontSize: '14px', whiteSpace: 'nowrap'}}>
                                        Showing {startIndex + 1}-{Math.min(endIndex, totalProducts)} of {totalProducts} (Page {currentPage}/{totalPages})
                                    </span>
                                </div>
                                
                                {/* Right: Pagination controls */}
                                <Pagination size="sm" className="mb-0" style={{flexWrap: 'wrap'}}>
                                        <PaginationItem disabled={currentPage === 1}>
                                            <PaginationLink 
                                                previous 
                                                onClick={() => setCurrentPage(currentPage - 1)}
                                                style={{ 
                                                    padding: '8px 12px',
                                                    border: '1px solid #dee2e6',
                                                    color: currentPage === 1 ? '#6c757d' : '#007bff'
                                                }}
                                            >
                                                <i className="fa fa-chevron-left" style={{fontSize: '12px'}}></i>
                                            </PaginationLink>
                                        </PaginationItem>
                                        
                                        {getPaginationPages().map((page, index) => (
                                            page === '...' ? (
                                                <PaginationItem key={`ellipsis-${index}`} disabled>
                                                    <PaginationLink 
                                                        style={{ padding: '8px 12px', border: 'none' }}
                                                    >
                                                        ...
                                                    </PaginationLink>
                                                </PaginationItem>
                                            ) : (
                                                <PaginationItem key={page} active={currentPage === page}>
                                                    <PaginationLink 
                                                        onClick={() => setCurrentPage(page)}
                                                        style={{ 
                                                            padding: '8px 12px',
                                                            border: '1px solid #dee2e6',
                                                            backgroundColor: currentPage === page ? '#007bff' : 'transparent',
                                                            color: currentPage === page ? 'white' : '#007bff',
                                                            fontWeight: currentPage === page ? 'bold' : 'normal',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {page}
                                                    </PaginationLink>
                                                </PaginationItem>
                                            )
                                        ))}
                                        
                                        <PaginationItem disabled={currentPage === totalPages}>
                                            <PaginationLink 
                                                next 
                                                onClick={() => setCurrentPage(currentPage + 1)}
                                                style={{ 
                                                    padding: '8px 12px',
                                                    border: '1px solid #dee2e6',
                                                    color: currentPage === totalPages ? '#6c757d' : '#007bff'
                                                }}
                                            >
                                                <i className="fa fa-chevron-right" style={{fontSize: '12px'}}></i>
                                            </PaginationLink>
                                        </PaginationItem>
                                    </Pagination>
                            </div>
                        )}
                    </React.Fragment>
                )}

                {/* Filter Sidebar */}
                {isFilterOpen && (
                    <div className="filter-sidebar-overlay" onClick={() => {
                        // Reset input field to match applied filters when closing without applying
                        setSearchTerm(appliedSearchTerm);
                        setIsFilterOpen(false);
                    }}>
                        <div className="filter-sidebar" onClick={(e) => e.stopPropagation()}>
                            <div className="filter-sidebar-header">
                                <h5 className="filter-sidebar-title">Filter</h5>
                                <Button
                                    className="close-filter-btn"
                                    color="link"
                                    onClick={() => {
                                        // Reset input field to match applied filters when closing without applying
                                        setSearchTerm(appliedSearchTerm);
                                        setIsFilterOpen(false);
                                    }}
                                >
                                    <i className="fa fa-times"></i>
                                </Button>
                            </div>

                            <div className="filter-sidebar-content">
                                {/* Search Section */}
                                <div className="filter-section">
                                    <label className="filter-label">Search</label>
                                    <Input
                                        type="text"
                                        placeholder="Search the product"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="filter-input"
                                    />
                                </div>
                            </div>

                            <div className="filter-sidebar-footer">
                                <Button
                                    className="clear-all-btn"
                                    color="secondary"
                                    outline
                                    onClick={handleClearFilters}
                                    style={{
                                        flex: 1,
                                        marginRight: '8px'
                                    }}
                                >
                                    Clear All
                                </Button>
                                <Button
                                    className="apply-filters-btn"
                                    color="primary"
                                    onClick={handleApplyFilters}
                                    style={{
                                        flex: 1
                                    }}
                                >
                                    Apply Filters
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment>
    );
}

export default AIAssistantProducts;
